/**
 * Indirect loader for `vite-plugin-css-injected-by-js` (ESM-only since v5).
 *
 * ## Why this file exists
 *
 * 1. Iris remotes must inject CSS into the JS bundle. The host iframe's CSP
 *    blocks external stylesheets, so a separate `.css` file leaves the app
 *    unstyled. Vite 8/Rolldown stopped emitting CSS the way Rollup did, so we
 *    need this plugin (v5+) to inline it.
 * 2. The plugin only publishes an `import` export condition. A plain
 *    `import … from "vite-plugin-css-injected-by-js"` (or a static `require`)
 *    fails under Nx's CJS executor hooks, which rewrite dynamic `import()` into
 *    `require()` and hit `ERR_PACKAGE_PATH_NOT_EXPORTED`.
 *
 * This module therefore resolves the package's ESM entry from its manifest and
 * loads it via an indirect `new Function("return import(...)")` so Node really
 * does an ESM import.
 *
 * ## When to delete this file
 *
 * Replace the call site in `getTrackunitIrisAppVitePlugins.ts` with a normal
 * `import cssInjectedByJsPlugin from "vite-plugin-css-injected-by-js"` and
 * delete this module once **all** of the following are true:
 *
 * - Nx executors for this package run as native ESM (no CJS rewrite of
 *   `import()`), **or** the plugin ships a CJS/`require` export again; and
 * - A plain static import of the plugin works inside
 *   `yarn nx build <any-iris-app>` (Iris apps still load with styles).
 *
 * Until then, do not "simplify" this back to a static import — that is the
 * regression that left Iris apps blank after the Vite 8 upgrade.
 */
import * as fs from "node:fs";
import * as path from "node:path";
import { pathToFileURL } from "node:url";
// Type-only import: erased at compile time so Nx's CJS executor never emits a
// require() for this ESM-only package, while the dependency checker still sees
// it as used (the runtime value is loaded via file URL below).
import type cssInjectedByJsPlugin from "vite-plugin-css-injected-by-js";

type CssInjectedByJsPluginFactory = typeof cssInjectedByJsPlugin;

const PACKAGE_NAME = "vite-plugin-css-injected-by-js";
const PREFERRED_CONDITIONS = ["import", "module"];
// "require" would resolve to a CJS build that the ESM import below cannot use.
const IGNORED_CONDITIONS = new Set(["require", "types"]);

const isRecord = (value: unknown): value is Record<string, unknown> => typeof value === "object" && value !== null;

/**
 * Find the installed package directory by walking node_modules upwards.
 *
 * require.resolve() cannot be used here: the package only publishes an
 * "import" export condition, so CJS resolution fails with
 * ERR_PACKAGE_PATH_NOT_EXPORTED.
 */
function findPackageDirFrom(startDir: string): string | undefined {
  let dir = path.resolve(startDir);

  for (;;) {
    const candidate = path.join(dir, "node_modules", PACKAGE_NAME);
    if (fs.existsSync(path.join(candidate, "package.json"))) {
      return candidate;
    }

    const parent = path.dirname(dir);
    if (parent === dir) {
      return undefined;
    }
    dir = parent;
  }
}

/**
 * Search this module's own tree before the consumer's workspace root.
 *
 * Under isolated node_modules layouts (pnpm, Yarn's `nodeLinker: pnpm`) this
 * package is installed beside the SDK and never appears at the consumer root,
 * so a root-only walk would fail for published consumers.
 */
export const defaultSearchRoots = (workspaceRoot: string): ReadonlyArray<string> => [__dirname, workspaceRoot];

function findPackageDir(searchRoots: ReadonlyArray<string>): string | undefined {
  for (const root of searchRoots) {
    const found = findPackageDirFrom(root);
    if (found) {
      return found;
    }
  }
  return undefined;
}

/** Walk nested export conditions, preferring the ESM ones. */
function pickEsmEntry(target: unknown): string | undefined {
  if (typeof target === "string") {
    return target;
  }
  if (!isRecord(target)) {
    return undefined;
  }

  const remaining = Object.keys(target).filter(
    condition => !PREFERRED_CONDITIONS.includes(condition) && !IGNORED_CONDITIONS.has(condition)
  );

  for (const condition of [...PREFERRED_CONDITIONS, ...remaining]) {
    const entry = pickEsmEntry(target[condition]);
    if (entry) {
      return entry;
    }
  }
  return undefined;
}

/**
 * Resolve the absolute ESM entry point of the installed package.
 *
 * The entry is read from the package manifest so that a renamed or relocated
 * published entry point does not break the build.
 */
export function resolveCssInjectedByJsPluginEntry(
  workspaceRoot: string,
  searchRoots: ReadonlyArray<string> = defaultSearchRoots(workspaceRoot)
): string {
  const packageDir = findPackageDir(searchRoots);

  if (!packageDir) {
    throw new Error(`${PACKAGE_NAME} not found in node_modules under ${searchRoots.join(", ")}. Run yarn install.`);
  }

  const manifest: unknown = JSON.parse(fs.readFileSync(path.join(packageDir, "package.json"), "utf-8"));

  if (!isRecord(manifest)) {
    throw new Error(`${PACKAGE_NAME} has an unreadable package.json in ${packageDir}.`);
  }

  // "exports" is either a bare entry or a subpath map holding the root entry.
  const exportsField = manifest.exports;
  const rootExport = isRecord(exportsField) ? exportsField["."] : exportsField;

  const entry =
    pickEsmEntry(rootExport) ?? pickEsmEntry(manifest.module) ?? pickEsmEntry(manifest.main) ?? "dist/index.js";
  const entryPath = path.join(packageDir, entry);

  if (!fs.existsSync(entryPath)) {
    throw new Error(`${PACKAGE_NAME} entry point not found at ${entryPath}. Run yarn install.`);
  }

  return entryPath;
}

/**
 * Load the CSS-injection plugin factory. See the file-level comment for why
 * this cannot be a static import, and what has to change before it can be.
 */
export async function loadCssInjectedByJsPlugin(workspaceRoot: string): Promise<CssInjectedByJsPluginFactory> {
  const entryPath = resolveCssInjectedByJsPluginEntry(workspaceRoot);

  const importEsm = new Function("specifier", "return import(specifier)");
  const mod: unknown = await importEsm(pathToFileURL(entryPath).href);
  const factory = isRecord(mod) ? mod.default : undefined;

  if (typeof factory !== "function") {
    throw new Error(`${PACKAGE_NAME} at ${entryPath} did not export a plugin factory as its default export.`);
  }

  return options => factory(options);
}
